import { useEffect, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CommandPalette } from '@backrs_ui/grow-ui/src/components/CommandPalette';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { useNotice } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/NoticeHost';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import {
  BATCH_LANDED,
  isAddableCohort,
  useAddCohortMembershipsMutation,
  useGetRosterCohortsQuery,
  useRemoveCohortMembershipsMutation,
  type AdminCohort,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { cohortOutcome, landedUnreadableMessage } from './cohortOutcome';
import { cohortSeats, knownSeats, type SeatHolder } from './memberSeats';
import { cohortLabel } from '../namedRefLabel';

const { seatActions } = appStrings.modules.coachingDashboard;
const strings = seatActions.cohortPalette;

export type CohortMembershipMode = 'add' | 'remove';

type CohortMembershipPaletteProps = Readonly<{
  open: boolean;
  onClose: () => void;
  mode: CohortMembershipMode;
  holder: SeatHolder;
}>;

const partition = (
  cohorts: readonly AdminCohort[],
  holder: SeatHolder,
): Readonly<{ joined: AdminCohort[]; joinable: AdminCohort[] }> => {
  const held = new Set((holder.cohorts ?? []).map((cohort) => cohort.id));

  return {
    joined: cohorts.filter((cohort) => held.has(cohort.id)),
    joinable: cohorts.filter((cohort) => !held.has(cohort.id) && isAddableCohort(cohort)),
  };
};

export const CohortMembershipPalette = ({
  open,
  onClose,
  mode,
  holder,
}: CohortMembershipPaletteProps) => {
  const notice = useNotice();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminCohort | null>(null);

  const [addMemberships, addState] = useAddCohortMembershipsMutation();
  const [removeMemberships, removeState] = useRemoveCohortMembershipsMutation();

  const seats = cohortSeats(knownSeats(holder.seats));
  const rosterId = seats.length === 0 ? null : seats[0].roster_id;

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(null);
    }
  }, [open]);

  const cohorts = useGetRosterCohortsQuery(open && rosterId !== null ? { rosterId } : skipToken);

  const { joined, joinable } = partition(cohorts.data?.data ?? [], holder);
  const offered = mode === 'add' ? joinable : joined;
  const matching = offered.filter((cohort) =>
    cohortLabel(cohort).toLowerCase().includes(search.trim().toLowerCase()),
  );

  const isWriting = addState.isLoading || removeState.isLoading;

  const emptyStateMessage = (() => {
    if (cohorts.isError) return noticeForError(refusalProse(cohorts.error), strings.error);
    if (cohorts.isFetching) return strings.loading;

    return mode === 'add' ? strings.addEmpty : strings.removeEmpty;
  })();

  const submit = async () => {
    if (rosterId === null || selected === null || isWriting) return;

    const name = cohortLabel(selected);
    const failed = mode === 'add' ? strings.addFailed : strings.removeFailed;
    const door = mode === 'add' ? addMemberships : removeMemberships;

    let results;

    // Only the write is guarded: a throw from the reporting below would otherwise be
    // announced as a refusal of a write that already committed.
    try {
      results = await door({
        rosterId,
        cohortId: selected.id,
        rosterSeatIds: seats.map((seat) => seat.seat_id),
      }).unwrap();
    } catch (error) {
      const landed = landedUnreadableMessage(error);

      if (landed !== null) {
        onClose();
        notice.announce(landed);

        return;
      }

      notice.announce(noticeForError(refusalProse(error), failed), 'error');

      return;
    }

    const outcome = cohortOutcome({
      results,
      holder,
      eligible: cohortSeats,
      landedWord: mode === 'add' ? BATCH_LANDED.add : BATCH_LANDED.remove,
      cohortName: name,
      voice: 'holder',
    });

    onClose();
    notice.announce(outcome.message, outcome.severity);
  };

  return (
    <CommandPalette<AdminCohort>
      open={open}
      onClose={onClose}
      title={mode === 'add' ? strings.addTitle(holder.name) : strings.removeTitle(holder.name)}
      searchPlaceholder={strings.searchPlaceholder}
      searchValue={search}
      onSearchChange={(value) => {
        setSearch(value);
        setSelected(null);
      }}
      items={matching}
      getItemKey={(cohort) => String(cohort.id)}
      renderItem={(cohort) => (
        <Typography
          id={`coaching-dashboard-cohort-${mode}-option-${cohort.id}`}
          variant="text-sm-regular"
        >
          {cohortLabel(cohort)}
        </Typography>
      )}
      selectedItem={selected}
      onItemSelect={setSelected}
      loading={cohorts.isFetching}
      emptyStateMessage={emptyStateMessage}
      actions={[
        {
          id: `coaching-dashboard-cohort-${mode}-submit`,
          label: mode === 'add' ? seatActions.addToCohort : seatActions.removeFromCohort,
          disabled: selected === null || isWriting,
          onClick: () => void submit(),
        },
      ]}
    />
  );
};
